/**
 * AutoPilot — the `\` hands-free mode.
 *
 * While active it re-rolls a fresh random effect set on every beat, so the
 * instrument plays itself in time with the tempo. It reads `tempo.bpm` /
 * `tempo.anchorMs` off the bus (the same signals the tempo indicator uses) and
 * counts elapsed beats per-frame; each time the beat index advances it fires
 * `onBeat` (once, even if several beats were skipped between frames). If no tempo
 * has been tapped yet it falls back to a steady default cadence so `\` still does
 * something without Space.
 *
 * When the tempo grid moves (a re-tap shifts the anchor, or the bpm changes), it
 * re-locks to the new grid on the next tick instead of stalling — so it never
 * appears to "turn off", and the next roll lands on the next downbeat of the new
 * tempo.
 *
 * Like the other controllers it observes the bus and emits callbacks; it owns no
 * effect state itself — main wires `onBeat`/`onStop` to InputController's
 * rollAuto()/stopAuto(), so the whole thing routes through the same random
 * primitives as `~`.
 */

import { ParamBus } from '../state/paramBus';

/** Cadence used until a tempo is tapped (120 bpm = one roll every 500 ms). */
export const DEFAULT_BPM = 120;

export interface AutoPilotCallbacks {
  onBeat: () => void;
  onStop: () => void;
}

export class AutoPilot {
  private active = false;
  /** Set on activation so the first tick fires immediately and seeds the count. */
  private pendingStart = false;
  /** Set when the tempo grid moved, so the next tick re-locks without firing. */
  private needsResync = false;
  /** Integer beat index seen at the previous tick, for advance detection. */
  private lastBeat = 0;
  /** Timebase when no real tempo is set; captured on activation. */
  private fallbackAnchor = 0;

  private bpm = 0;
  private anchor = 0;

  constructor(
    private readonly bus: ParamBus,
    private readonly callbacks: AutoPilotCallbacks,
  ) {
    this.bus.subscribe((change) => {
      if (change.key === 'tempo.bpm') { this.bpm = change.value; this.needsResync = true; }
      else if (change.key === 'tempo.anchorMs') { this.anchor = change.value; this.needsResync = true; }
    });
  }

  get isActive(): boolean {
    return this.active;
  }

  /** Flip auto-pilot; returns the new active state. */
  toggle(): boolean {
    if (this.active) {
      this.stop();
      return false;
    }
    this.active = true;
    this.pendingStart = true;
    return true;
  }

  /** Turn auto-pilot off (no-op if already off), clearing its effect set. */
  stop(): void {
    if (!this.active) return;
    this.active = false;
    this.pendingStart = false;
    this.callbacks.onStop();
  }

  /** Advance the beat clock; `nowMs` is a monotonic clock (performance.now). */
  tick(nowMs: number): void {
    if (!this.active) return;
    if (this.pendingStart) {
      this.pendingStart = false;
      this.needsResync = false; // seeding lastBeat below covers the resync
      this.fallbackAnchor = nowMs;
      this.lastBeat = this.beatIndex(nowMs);
      this.callbacks.onBeat(); // roll immediately, don't wait a whole beat
      return;
    }
    if (this.needsResync) {
      // The tempo grid moved (re-tap / bpm change): re-lock to it without firing,
      // so the next roll lands on the next downbeat of the new tempo.
      this.needsResync = false;
      this.lastBeat = this.beatIndex(nowMs);
      return;
    }
    const beat = this.beatIndex(nowMs);
    if (beat > this.lastBeat) {
      this.lastBeat = beat;
      this.callbacks.onBeat(); // a new beat began — roll once
    }
  }

  /** How many whole beats have elapsed since the (effective) anchor. */
  private beatIndex(nowMs: number): number {
    const hasTempo = this.bpm > 0 && this.anchor > 0;
    const bpm = hasTempo ? this.bpm : DEFAULT_BPM;
    const anchor = hasTempo ? this.anchor : this.fallbackAnchor;
    const beatMs = 60000 / bpm;
    return Math.floor((nowMs - anchor) / beatMs);
  }
}
